
import { toast } from 'sonner';
import { useClubs, Club } from '@/hooks/useClubs';
import { useClubForm } from '@/hooks/useClubForm';
import { useClubOperations } from '@/hooks/useClubOperations';
import { useClubUI } from '@/hooks/useClubUI';

export const useClubManagement = () => {
  const { clubs, loading, fetchClubs } = useClubs();
  const clubForm = useClubForm();
  const clubOperations = useClubOperations();
  const clubUI = useClubUI();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!clubForm.clubName.trim()) return;

    clubOperations.setIsSubmitting(true);
    try {
      const formData = {
        clubName: clubForm.clubName,
        clubWebsite: clubForm.clubWebsite,
        clubLocation: clubForm.clubLocation,
        clubProfilePicture: clubForm.clubProfilePicture,
        clubSport: clubForm.clubSport
      };

      if (clubUI.editingClub) {
        await clubOperations.updateClub(clubUI.editingClub.id, formData);
        toast.success('Club updated successfully');
      } else {
        await clubOperations.createClub(formData);
        toast.success('Club created successfully');
      }

      clubUI.setIsDialogOpen(false);
      clubUI.setEditingClub(null);
      clubForm.resetForm();
      
      // Manually refresh the clubs list since we're using manual state management
      await fetchClubs();
      
    } catch (error) {
      console.error('Error saving club:', error);
      toast.error('Failed to save club');
    } finally {
      clubOperations.setIsSubmitting(false);
    }
  };

  const handleEdit = (club: Club) => {
    const clubData = {
      ...club,
      website: club.website || '',
      location: club.location || '',
      sport: club.sport || 'Volleyball',
      profile_picture_url: club.profile_picture_url || '',
      created_at: club.created_at || '',
      organization_id: club.organization_id || ''
    };
    clubUI.setEditingClub(clubData);
    clubForm.setFormData(clubData);
    clubUI.setIsDialogOpen(true);
  };

  const handleDeleteClick = (club: Club) => {
    const clubData = {
      ...club,
      website: club.website || '',
      location: club.location || '',
      sport: club.sport || 'Volleyball',
      profile_picture_url: club.profile_picture_url || '',
      created_at: club.created_at || '',
      organization_id: club.organization_id || ''
    };
    clubUI.setClubToDelete(clubData);
    clubUI.setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!clubUI.clubToDelete) {
      return;
    }
    clubOperations.setIsDeleting(true);

    try {
      await clubOperations.deleteClub(clubUI.clubToDelete.id);
      
      toast.success('Club deleted successfully');
      
      // Close the dialog first
      clubUI.setDeleteDialogOpen(false);
      clubUI.setClubToDelete(null);
      
      // Manually refresh the clubs list since we're using manual state management
      await fetchClubs();
      
    } catch (error) {
      console.error('Error during club deletion:', error);
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';
      if (errorMessage.includes('Cannot delete club with existing')) {
        toast.error(errorMessage);
      } else {
        toast.error(`Failed to delete club: ${errorMessage}`);
      }
    } finally {
      clubOperations.setIsDeleting(false);
    }
  };

  const handleDialogClose = () => {
    clubUI.handleDialogClose();
    clubForm.resetForm();
  };

  return {
    // Data
    clubs,
    loading,
    
    // Form state
    isDialogOpen: clubUI.isDialogOpen,
    editingClub: clubUI.editingClub,
    clubName: clubForm.clubName,
    clubWebsite: clubForm.clubWebsite,
    clubLocation: clubForm.clubLocation,
    clubProfilePicture: clubForm.clubProfilePicture,
    clubSport: clubForm.clubSport,
    isSubmitting: clubOperations.isSubmitting,
    
    // UI state
    expandedClubs: clubUI.expandedClubs,
    deleteDialogOpen: clubUI.deleteDialogOpen,
    clubToDelete: clubUI.clubToDelete,
    isDeleting: clubOperations.isDeleting,
    
    // Form setters
    setClubName: clubForm.setClubName,
    setClubWebsite: clubForm.setClubWebsite,
    setClubLocation: clubForm.setClubLocation,
    setClubProfilePicture: clubForm.setClubProfilePicture,
    setClubSport: clubForm.setClubSport,
    
    // Handlers
    handleSubmit,
    handleEdit,
    handleDeleteClick,
    handleConfirmDelete,
    handleDialogClose,
    handleDeleteDialogClose: clubUI.handleDeleteDialogClose,
    toggleClubExpansion: clubUI.toggleClubExpansion,
    setIsDialogOpen: clubUI.setIsDialogOpen
  };
};
